import {
    Alert, Box, Chip, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from "@mui/material"
import DownloadIcon from "@mui/icons-material/Download"
import { formatCount } from "./UsageTable"
import { downloadCsv } from "./exportCsv"
import dayjs from "dayjs"

const primaryTypes = [
    ["administrador", "Administrador"], ["supervisor", "Supervisor"], ["operador", "Operador"],
]
const additionalTypes = [["other", "Outros / sem perfil"], ["", "Histórico sem perfil"]]

const ModuleAccessByType = ({ enterprise, rows }) => {
    const totals = enterprise.accesses_by_type || {}
    const types = [...primaryTypes, ...additionalTypes.filter(([key]) => (totals[key] || 0) > 0)]
    const modules = rows.filter(row => row.access_tracked).sort((a, b) => b.accesses - a.accesses)
    const formatDate = value => value ? dayjs(value).format("DD/MM/YYYY") : "—"

    return (
        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderColor: "#21446C", borderRadius: 2, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography variant="h5" sx={{ color: "#21446C", fontWeight: 700 }}>Módulos por tipo de usuário</Typography>
                <IconButton size="small" aria-label="Baixar CSV: acessos por tipo de usuário" title="Baixar CSV: acessos por tipo de usuário" onClick={() => downloadCsv("kwik-acessos-por-tipo.csv", [
                    { key: "label", label: "Módulo" }, { key: "enabled", label: "Habilitado" }, { key: "accesses", label: "Total" },
                    { key: "first_access", label: "Primeiro acesso" }, { key: "last_access", label: "Último acesso" },
                    ...types.map(([key, label]) => ({ key: `role_${key || "history"}`, label })),
                ], modules.map(row => ({ ...row, first_access: formatDate(row.first_access), last_access: formatDate(row.last_access), ...Object.fromEntries(types.map(([key]) => [`role_${key || "history"}`, row.accesses_by_type?.[key] || 0])) })))}><DownloadIcon fontSize="small" /></IconButton>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                Esta tabela mostra o acesso às guias por Administradores, Supervisores e Operadores, módulo a módulo.
                Cada clique conta, mesmo quando o módulo está desabilitado. O uso operacional aparece separado nas colunas de uso e adoção.
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, gap: 2, mb: 2 }}>
                {primaryTypes.map(([key, label]) => (
                    <Box key={key} sx={{ p: 2, bgcolor: "#F1F5F9", borderRadius: 1 }}>
                        <Typography variant="subtitle2">{label}</Typography>
                        <Typography variant="h4" sx={{ color: "#21446C", fontWeight: 700 }}>{formatCount(totals[key] || 0)}</Typography>
                        <Typography variant="caption" color="text.secondary">acessos aos módulos</Typography>
                    </Box>
                ))}
            </Box>
            {!enterprise.module_accesses && <Alert severity="info" sx={{ mb: 2 }}>Nenhum acesso aos módulos registrado neste período.</Alert>}
            <TableContainer sx={{ overflowX: "auto" }}>
                <Table size="small" aria-label="Acessos por módulo e tipo de usuário" sx={{ minWidth: 760 }}>
                    <TableHead sx={{ bgcolor: "#F1F5F9" }}>
                        <TableRow>
                            <TableCell>Módulo</TableCell>
                            <TableCell align="center">Habilitado agora</TableCell>
                            <TableCell align="right">Total de acessos</TableCell>
                            <TableCell align="center">Primeiro acesso</TableCell>
                            <TableCell align="center">Último acesso</TableCell>
                            {types.map(([key, label]) => <TableCell key={key} align="right" sx={{ fontWeight: 700 }}>{label}</TableCell>)}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {modules.map(row => (
                            <TableRow key={row.id} hover>
                                <TableCell component="th" scope="row">{row.label}</TableCell>
                                <TableCell align="center"><Chip size="small" variant="outlined" color={row.enabled ? "success" : "default"} label={row.enabled ? "Sim" : "Não"} /></TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCount(row.accesses)}</TableCell>
                                <TableCell align="center">{formatDate(row.first_access)}</TableCell>
                                <TableCell align="center">{formatDate(row.last_access)}</TableCell>
                                {types.map(([key]) => <TableCell key={key} align="right" sx={{ color: "#21446C", fontWeight: 600 }}>{formatCount(row.accesses_by_type?.[key] || 0)}</TableCell>)}
                            </TableRow>
                        ))}
                        <TableRow sx={{ bgcolor: "#F1F5F9", "& th, & td": { fontWeight: 700 } }}>
                            <TableCell component="th" scope="row" colSpan={2}>Total no período</TableCell>
                            <TableCell align="right">{formatCount(enterprise.module_accesses)}</TableCell>
                            <TableCell colSpan={2} />
                            {types.map(([key]) => <TableCell key={key} align="right">{formatCount(totals[key] || 0)}</TableCell>)}
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                O perfil é o do momento do acesso. Usuários com mais de um grupo contam uma vez, priorizando Administrador, Supervisor e Operador.
                {(totals.other || 0) > 0 && " Outros / sem perfil reúne usuários sem esses grupos."}
                {(totals[""] || 0) > 0 && " Histórico sem perfil preserva os acessos antigos, sem atribuí-los a usuários."}
            </Typography>
        </Paper>
    )
}

export default ModuleAccessByType
